import { GlobalMessages } from '@shared/localization/messages';
import type { Ref } from 'react';
import Select, { type SelectInstance } from 'react-select';

import IconSearch from '@/assets/Icons/IconSearch';
import { useMessages } from '@/hooks/messages';
import type { SelectOption } from '@/types';

interface LiveSearchSelectProps {
  isMulti?: boolean;
  defaultValue?: SelectOption;
  value?: SelectOption | SelectOption[]; // TODO: value: SelectOption['value']
  options: SelectOption[];
  name?: string;
  placeholder?: string;
  onInputChange?: (input: string) => void;
  onValueSelect: (selectedValue: string | string[] | null) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  'data-testid'?: string;
  ref?: Ref<SelectInstance>;
}

const selectComponents = {
  DropdownIndicator: () => (
    <button
      type='button'
      className='inset-y-0 flex h-9 w-9 items-center justify-center rounded-full p-0 ltr:right-1 rtl:left-1'
    >
      <IconSearch />
    </button>
  ),
  IndicatorSeparator: () => null,
};

const LiveSearchSelect = ({
  isMulti,
  defaultValue,
  value,
  options,
  name,
  onInputChange,
  onValueSelect,
  onBlur,
  onFocus,
  isLoading,
  isDisabled,
  placeholder: placeholderProp,
  'data-testid': dataTestId,
  ref,
}: LiveSearchSelectProps) => {
  const globalMessages = useMessages(GlobalMessages);

  const handleOnChange = (selectedValue: SelectOption | SelectOption[]) => {
    if (selectedValue) {
      onValueSelect(
        Array.isArray(selectedValue) ? selectedValue.map((o) => o.value) : selectedValue.value,
      );
    } else {
      onValueSelect(Array.isArray(selectedValue) ? [] : null);
    }
  };

  return (
    <div className='relative' data-testid={dataTestId}>
      <Select
        // Portal the menu to <body> and position it fixed so it escapes the
        // modal's overflow:hidden AND its transform/stacking context — without a
        // portal target a fixed-positioned menu mispositions inside the modal.
        menuPosition='fixed'
        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
        ref={ref}
        name={name}
        placeholder={placeholderProp ?? globalMessages.search()}
        isSearchable
        isClearable
        openMenuOnFocus
        noOptionsMessage={() => globalMessages.noDataFound()}
        loadingMessage={() => globalMessages.loading()}
        isMulti={isMulti}
        value={value}
        defaultValue={defaultValue}
        components={selectComponents}
        onInputChange={onInputChange}
        options={options}
        onChange={(selectedValue) => handleOnChange(selectedValue as SelectOption | SelectOption[])}
        onBlur={onBlur}
        onFocus={onFocus}
        isLoading={isLoading}
        isDisabled={isDisabled}
        classNamePrefix='custom-select'
        classNames={{
          control: () =>
            `outline-2 border-c1 !rounded-md focus:border-c1 dark:!border-c1 dark:!focus:border-white  mt-1 bg-white placeholder:tracking-wider pr-1 pt-0 pl-1 text-sm font-medium h-2 text-left'  ${isDisabled ? 'cursor-not-allowed opacity-50' : ''}`,
          multiValue: () => '!bg-c1 !rounded-md',
          multiValueLabel: () => 'text-sm font-medium !text-extraDark dark:!text-white',
          multiValueRemove: () =>
            'cursor-pointer hover:!bg-white dark:hover:!bg-c4 hover:text-extraDark dark:hover:text-red hover:rounded-md',
        }}
        styles={{
          menu: (provided) => ({
            ...provided,
            width: 'max-content',
            minWidth: '100%',
            zIndex: 1000,
          }),
          // Must sit above the Mantine modal (z-index ≥ 10009) since the menu is
          // portaled to <body>, otherwise it renders behind the modal overlay.
          menuPortal: (provided) => ({
            ...provided,
            zIndex: 20000,
          }),
        }}
      />
    </div>
  );
};

export default LiveSearchSelect;
